"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import { Fragment } from "react";
import { CTAButton } from "@/shadcn/ui/button";

export default function StepsSection({
  data,
  className,
}: {
  data: PageAffiliateSchema;
  className?: string;
}) {
  const rightItemVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.2 + i * 0.1,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <section
      className={cn(
        "relative overflow-clip pt-[8rem] lg:pt-[15.25rem] pb-[7rem] lg:pb-[20rem]",
        className,
      )}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
            )}
          />
        ))}
      </div>

      <div className="w-[min(157.125rem,100%)] mx-auto grid grid-cols-1 lg:grid-cols-[54.75rem_auto] gap-[3.5rem] lg:gap-[10.5rem] bg-white border border-[#E5E5E5] px-[2rem] py-[4.5rem] lg:p-[9.38rem] relative">
        <div className="flex flex-col items-center lg:items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <ChipSerif text={data.stepsChip.text} />
          </motion.div>

          {data?.stepsText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext lg:richtext-start"
            >
              <PortableText
                value={data.stepsText}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.stepsCta && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="mt-[3rem]"
            >
              <CTAButton data={data.stepsCta} className="" />
            </motion.div>
          )}
        </div>
        <div className="flex flex-col gap-[2.25rem]">
          {data.stepsItems &&
            data.stepsItems.length > 0 &&
            data.stepsItems.map((item, index) => (
              <motion.div
                key={"step" + index}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={rightItemVariants}
                className="bg-[#F6F6F6] px-[4rem] py-[3.5rem] flex items-center gap-[2rem] rounded-[1.375rem]"
              >
                <div
                  className="px-[2rem] py-[1.5rem] flex items-center justify-center bg-[#103037] w-[6rem] h-[6rem] rounded-[1.375rem]
                  text-white text-[3rem] font-medium leading-none shrink-0"
                >
                  {index + 1}
                </div>
                <div
                  className="prose-p:mb-0 prose-strong:text-[#2E3436] prose-strong:font-medium prose-strong:text-[2.5rem]
                  prose-p:text-[2rem] prose-p:text-[#1C1F22]/70"
                >
                  <PortableText
                    value={item.text}
                    components={richTextComponent}
                  />
                </div>
              </motion.div>
            ))}
        </div>
      </div>
    </section>
  );
}
